/**
 * Firebase client initialization.
 *
 * This module reads exclusively from environment variables (see
 * .env.example). It never contains real project values. Until
 * VITE_FIREBASE_* variables are set, `isFirebaseConfigured` is false and
 * every consumer (auth hooks, Firestore calls) must fail safely instead of
 * silently pretending to work — see AUTH_NOT_CONFIGURED handling in
 * src/lib/firebase/auth.ts.
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

// TEMPORARY DEV-ONLY DIAGNOSTIC — safe to leave in, but remove once the
// "Sign-in isn't connected" investigation is resolved.
//
// `import.meta.env.DEV` is a build-time constant Vite sets to `false` in
// any production build (`vite build`), so this entire block is dead code
// and gets stripped out of dist/ automatically — it can NEVER run in
// production regardless of whether this file ships as-is. It never logs
// actual values, only whether each variable is present/non-empty, so it's
// safe to have open in a real browser console.
//
// If this logs `isFirebaseConfigured: true` but the UI still shows the
// "not connected" banner, the problem is NOT this file — it's a stale
// bundle being served (old `npm run preview` / browser cache / a
// different project folder actually being served). If it logs `false`,
// check which of the six lines below says `false` — that's the exact
// missing/misnamed variable.
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log('[KETIX3D Firebase config diagnostic — dev only, no values logged]', {
    VITE_FIREBASE_API_KEY: Boolean(firebaseConfig.apiKey),
    VITE_FIREBASE_AUTH_DOMAIN: Boolean(firebaseConfig.authDomain),
    VITE_FIREBASE_PROJECT_ID: Boolean(firebaseConfig.projectId),
    VITE_FIREBASE_STORAGE_BUCKET: Boolean(firebaseConfig.storageBucket),
    VITE_FIREBASE_MESSAGING_SENDER_ID: Boolean(firebaseConfig.messagingSenderId),
    VITE_FIREBASE_APP_ID: Boolean(firebaseConfig.appId),
    isFirebaseConfigured,
    resolvedProjectId: firebaseConfig.projectId || '(empty)',
  });
}

let app: FirebaseApp | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!isFirebaseConfigured) {
    throw new Error(
      'Firebase is not configured. Set VITE_FIREBASE_* variables in .env.local — see SETUP.md.'
    );
  }
  if (!app) {
    app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }
  return app;
}
